import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(searchParams.get("error") === "google_auth_failed" ? "Google sign-in failed. Please try again." : "");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // Server-side redirect flow: backend sends the browser to Google, then back to
    // /api/auth/google/callback, which sets our auth cookie and redirects home.
    window.location.href = `${API}/auth/google/login`;
  };

  return (
    <div className="min-h-screen w-full relative overflow-x-hidden text-white">
      <div
        className="fixed inset-0 z-[-1]"
        style={{
          backgroundImage: `url('/bg-hero.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-8">
        <h1
          className="text-4xl sm:text-5xl font-primary text-center mb-2"
          style={{ fontFamily: "Anton, sans-serif" }}
        >
          <span className="text-[#FF0055]">SIGN</span>{" "}
          <span className="text-[#00F0FF]">IN</span>
        </h1>
        <p className="text-[#A1A1AA] text-center mb-8">Welcome back, host</p>

        <div className="backdrop-blur-2xl bg-black/40 border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl w-full max-w-md">
          <button
            onClick={handleGoogleSignIn}
            type="button"
            className="w-full py-3 mb-4 bg-white text-black font-semibold rounded-xl hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3"
            data-testid="google-signin-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
          <p className="text-center text-[#A1A1AA] text-xs mb-2">
            By continuing, you agree to our{" "}
            <Link to="/terms" className="text-[#00F0FF] hover:underline">Terms</Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-[#00F0FF] hover:underline">Privacy Policy</Link>.
          </p>

          <div className="flex items-center gap-3 my-4">
            <div className="h-px bg-white/10 flex-1" />
            <span className="text-[#A1A1AA] text-sm">or</span>
            <div className="h-px bg-white/10 flex-1" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-all"
              data-testid="login-email-input"
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none transition-all"
              data-testid="login-password-input"
            />
            {error && (
              <div className="text-[#FF0055] text-center text-sm" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-[#00F0FF] text-black font-primary text-xl tracking-wide rounded-xl shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:scale-[1.02] active:scale-95 transition-all uppercase disabled:opacity-50"
              style={{ fontFamily: "Anton, sans-serif" }}
              data-testid="login-submit-button"
            >
              {loading ? "SIGNING IN..." : "SIGN IN"}
            </button>
          </form>

          <p className="text-center text-[#A1A1AA] text-sm mt-6">
            New here?{" "}
            <Link to="/signup" className="text-[#00F0FF] hover:underline" data-testid="go-to-signup-link">
              Create an account
            </Link>
          </p>
          <p className="text-center text-[#A1A1AA] text-sm mt-2">
            <Link to="/" className="hover:text-white">← Back to home</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
